// src/pages/DiscoveryInbox.jsx
// Competitor Discovery Inbox — candidates table + slide panel + run dialog.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshButton from '../components/common/RefreshButton';
import FilterPillRow from '../components/common/FilterPillRow';
import PageContainer from '../components/common/PageContainer';
import TableViewport from '../components/common/TableViewport';
import ViewHeader from '../components/ViewHeader';
import CandidateRow from '../components/discovery/CandidateRow';
import CandidatePanel from '../components/discovery/CandidatePanel';
import RunDialog from '../components/discovery/RunDialog';
import ScanBanner from '../components/discovery/ScanBanner';
import { getCandidates, getDiscoveryStats } from '../api/discovery';
import Panel from '../components/common/Panel';
import TableSkeleton from '../Skeletons/TableSkeleton';
import EmptyState from '../components/common/EmptyState';
import { FS, SP } from '../theme/tokens';

const STATUS_FILTERS = ['pending', 'approved', 'rejected', 'deferred'];

export default function DiscoveryInbox() {
  // 🔴 R16-Runde 2 (F1) / R15 W1: `candidates` startet auf `null`, nicht auf `[]`.
  // „Noch nie geladen" ist etwas anderes als „die Liste ist leer": eine berechtigt
  // leere Discovery-Inbox (Statusfilter ohne Treffer, frischer Lauf) laese mit `[]`
  // beim NAECHSTEN Neulauf als Erstaufbau, und der Knopf sperrte dann, statt zu
  // drehen. Startwert-Form wie `ops/PoAnomalies.jsx:132` (`useState(null)`).
  // 🔴 R15-Wiedervorlage 2, N1: die drei Lesestellen vertragen `null`, aber auf
  // ZWEI Wegen — der `firstLoad`-Ausdruck am Knopf VERGLEICHT darauf
  // (`candidates === null`), die zwei Renderstellen der Tabelle fahren
  // `(candidates ?? [])`. Nicht alle drei fahren `?? []`.
  const [candidates, setCandidates] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  // 🔴 R15-Wiedervorlage 2, W2 (Regel 22 Nr. 4 „Leer und Fehler sind verschiedene
  // Objekte"): der Fehlerpfad braucht einen EIGENEN Zustand. Ohne ihn faellt ein
  // gescheiterter Abruf in den Leerzweig und liest sich als „keine Kandidaten" —
  // eine bestaetigte Null auf einer Flaeche, die nichts gemessen hat. `candidates`
  // bleibt daneben `null` (der Knopf-Diskriminator oben); der Fehler steht hier.
  const [candidatesError, setCandidatesError] = useState(null);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [scanState, setScanState] = useState(null); // { sourceTier } | null
  const [statusFilter, setStatusFilter] = useState('pending');

  const fetchStats = useCallback(() => {
    setStatsLoading(true);
    getDiscoveryStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  const fetchCandidates = useCallback(() => {
    setCandidatesLoading(true);
    getCandidates({ status: statusFilter })
      .then(data => {
        const list = data?.candidates ?? data?.items ?? (Array.isArray(data) ? data : []);
        setCandidates(list);
        setCandidatesError(null);
      })
      .catch((err) => {
        console.error('[DiscoveryInbox] fetchCandidates failed:', err);
        // Ein gescheiterter Abruf ist „nie geladen", nicht „leer" — deshalb `null`
        // und nicht `[]`; der Grund steht daneben in `candidatesError`, damit die
        // Flaeche ihn zeigen kann (W2).
        // ⚠️ `ops/PoAnomalies.jsx:147` ist dafuer NICHT die Hausform (R15-Wiedervorlage
        // 2, W3): dort setzt der `catch` `setError(...)` und laesst `items` UNVERAENDERT
        // stehen. Beide vermeiden `[]`, aber nur diese Stelle setzt auf „nie geladen"
        // zurueck. Gemeinsam ist beiden nur der Startwert (`useState(null)`).
        setCandidates(null);
        setCandidatesError('Could not load discovery candidates.');
      })
      .finally(() => setCandidatesLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  function handleRefresh() {
    fetchCandidates();
    fetchStats();
  }

  function handleAfterAction() {
    // Close panel, refresh both lists
    setSelectedCandidate(null);
    fetchCandidates();
    fetchStats();
  }

  return (
    <PageContainer sx={{ pb: 6 }}>
      <ViewHeader
        title="Discovery Inbox"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Discovery Inbox' },
        ]}
        actions={[
          {
            key: 'trigger-run',
            label: 'Trigger run',
            icon: <PlayArrowIcon />,
            variant: 'contained',
            onClick: () => setRunDialogOpen(true),
          },
        ]}
      />

      {/* Scan banner — shown while/after a run is active */}
      {scanState && (
        <ScanBanner
          sourceTier={scanState.sourceTier}
          onDone={handleRefresh}
          onDismiss={() => setScanState(null)}
        />
      )}

      {/* Stats row.
          🔴 F10 (2026-10-07): das Orange HIER bleibt — es ist ein ZUSTAND, keine
          Auswahl. `pending > 0` heisst „es liegt Arbeit an" und faerbt die
          Zusammenfassung; die Auswahl darunter traegt seit demselben Tag die
          Hausform (`FilterPillRow`, `primary.main`). Vorher trugen beide Orange,
          und die Auswahl war von der Warnung nicht zu unterscheiden — genau der
          Fall, den F10 meint („Auswahl ist nie Akzentfarbe").
          ⚠️ Der Fotobefund nannte BEIDE als orange gefuellt; die Unterscheidung
          ist der Grund, warum nur einer bleibt. */}
      <Stack direction="row" spacing={1} sx={{ mb: SP.section }}>
        {statsLoading ? (
          <CircularProgress size={16} />
        ) : stats ? (
          <>
            <Chip
              data-testid="discovery-zusammenfassung"
              label={`${stats.pending ?? 0} pending`}
              color={stats.pending > 0 ? 'warning' : 'default'}
              size="small"
            />
            <Chip label={`${stats.total ?? 0} total`} size="small" />
          </>
        ) : null}
      </Stack>

      {/* Filter + refresh toolbar.
          🔴 F10: die gewaehlten Filter-Chips waren `Chip color={STATUS_CHIP_COLOR[s]}`
          — der STATUS-Akzent (warning/success/error) war damit zugleich die
          Auswahlfarbe. Das Haus hat fuer Auswahl genau eine Form: `FilterPillRow`
          mit `primary.main` als Fuellung (`GLASS_PILL_SX`, `aria-pressed`).
          Dieselbe Form wie die Schnellfilter in Produkten und Faellen. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <FilterPillRow
          alwaysShow
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_FILTERS.map(s => ({
            value: s,
            label: s.charAt(0).toUpperCase() + s.slice(1),
          }))}
        />
        <Box sx={{ flex: 1 }} />
        {/* 🔴 `firstLoad`: im Aufbau traegt die Tabelle ihren eigenen
            Ladehinweis, der Knopf dreht sich dann nicht mit (Anti-Pattern 11).
            🔴 Der Diskriminator ist `candidates === null` („noch nie geladen"),
            NICHT `candidates.length === 0` („Liste leer"): ein Neulauf einer
            berechtigt leeren Inbox muss drehen. Hergeleitet in
            `RefreshButton.jsx:26-34`, `pattern-library.md` und
            `component-registry.md`; hier stand bis R16-Runde 2 die verbotene Form.
            ⚠️ Der Aufbau zeigte bis K-S (UI-Durchgang 2) einen
            `CircularProgress size={32}` statt Skeletten — derselbe
            Anti-Pattern, eine Ebene hoeher. 🟢 Behoben: das Geruest steht jetzt
            in der Form der geladenen Tabelle (7 Spalten). Akte:
            `docs/backlog-auto-repair/2026-10-07-aufbauzustand-ist-ein-spinner.md`. */}
        <RefreshButton
          ariaLabel="Refresh discovery candidates"
          loading={candidatesLoading}
          firstLoad={candidatesLoading && candidates === null}
          onClick={handleRefresh}
        />
      </Stack>

      {/* Candidates table */}
      <Panel pad="none">
        {candidatesLoading ? (
          // K-S (UI-Durchgang 2): Seitenaufbau als Geruest der geladenen
          // Kandidatentabelle (7 Spalten), in derselben Bildlaufhuelle.
          <TableViewport>
            <TableSkeleton framed={false} columns={7} rows={5} ariaLabel="Loading discovery candidates" />
          </TableViewport>
        ) : candidatesError ? (
          /* 🔴 W2: der Fehlerzweig steht VOR dem Leerzweig. Stuende er dahinter,
             faellt ein gescheiterter Abruf (`candidates === null`) in den
             Leerzweig und liest sich als „keine Kandidaten" — eine bestaetigte
             Null auf einer Flaeche, die nichts gemessen hat (Regel 22 Nr. 4).
             Der Wiederholen-Knopf ist ein BESCHRIFTETER Knopf, nicht
             `RefreshButton` — dessen `@dontUseFor` schliesst „Retry nach einem
             Fehler" aus. */
          <EmptyState
            reason="error"
            message={candidatesError}
            action={
              <Button variant="outlined" size="small" onClick={fetchCandidates}>
                Try again
              </Button>
            }
          />
        ) : (candidates ?? []).length === 0 ? (
          /* Leer heisst hier „nichts passt zu DIESER Auswahl": ein Statusfilter
             ist immer gesetzt, und der Nutzer kann ihn wechseln — deshalb
             `no-match` und nicht `no-data`. */
          <EmptyState reason="no-match" message={`No ${statusFilter} candidates`} />
        ) : (
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: FS.sm, fontWeight: 600 }}>Shop</TableCell>
                  <TableCell sx={{ fontSize: FS.sm, fontWeight: 600 }}>SKU</TableCell>
                  <TableCell sx={{ fontSize: FS.sm, fontWeight: 600 }}>Confidence</TableCell>
                  <TableCell sx={{ fontSize: FS.sm, fontWeight: 600 }}>Dedup</TableCell>
                  <TableCell sx={{ fontSize: FS.sm, fontWeight: 600, textAlign: 'right' }}>Price</TableCell>
                  <TableCell sx={{ fontSize: FS.sm, fontWeight: 600 }}>Source</TableCell>
                  <TableCell sx={{ fontSize: FS.sm, fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(candidates ?? []).map(c => (
                  <CandidateRow
                    key={c.id}
                    candidate={c}
                    selected={selectedCandidate?.id === c.id}
                    onSelect={setSelectedCandidate}
                  />
                ))}
              </TableBody>
            </Table>
          </TableViewport>
        )}
      </Panel>

      {/* Detail panel */}
      <CandidatePanel
        candidate={selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        onApproved={handleAfterAction}
        onRejected={handleAfterAction}
        onDeferred={handleAfterAction}
      />

      {/* Run dialog */}
      <RunDialog
        open={runDialogOpen}
        onClose={() => setRunDialogOpen(false)}
        onRunStarted={({ sourceTier }) => {
          setScanState({ sourceTier });
        }}
      />
    </PageContainer>
  );
}
